import { AxiosInstance } from 'axios';
import { ArgumentValidator } from '../../helpers';
import { QueryStringBuilder } from '../helpers';
import {
    IEditorialListItem,
    IEditorialListItemsByUsersQueryResult,
    IEditorialListParticipantsByQueryRequest,
    IEditorialListStatus,
    IPagedList,
    IUser
} from '../models';
import { IEditorialListAddByQueryRequest } from '../models/editorial-list-add-by-query-request';

import { IEditorialListByQueryRequest } from '../models/editorial-list-by-query-request';
import { IEditorialListDeleteItemRequest } from '../models/editorial-list-delete-item-request';
import { IEditorialListNewItemRequest } from '../models/editorial-list-new-item-request';
import { IEditorialListNewReferenceRequest } from '../models/editorial-list-new-reference-request';
import { IEditorialListReferenceRequest } from '../models/editorial-list-reference-request';
import { IEditorialListUsersRequest } from '../models/editorial-list-users-request';
import { IPagedParticipantList } from '../models/activity/paged-participant-list';
import { IEditorialListAddByIdentifiersRequest } from '../models/editorial-list-add-by-identifiers-request';
import { IEditorialListItemsByIdentifiersResult } from '../models/editorial-list-items-by-identifiers-result';

export class EditorialListClient {

    constructor(private axiosClient: AxiosInstance) {

    }

    public get apiUrl() {
        return `/editorialList`;
    }

    public async removeAllItems(request: IEditorialListReferenceRequest): Promise<void> {

        ArgumentValidator.notNullOrEmpty(request, 'request');

        await this.axiosClient.delete(
            `${this.apiUrl}/items/${request.entityType}/${request.entityId}/${request.itemType}`
        );
    }

    public async removeItem(request: IEditorialListDeleteItemRequest): Promise<void> {

        ArgumentValidator.notNullOrEmpty(request, 'request');

        await this.axiosClient.delete(
            `${this.apiUrl}/items/${request.entityType}/${request.entityId}/${request.itemType}/${request.identifier}`
        );
    }

    public async listItems(request: IEditorialListByQueryRequest): Promise<IPagedList<IEditorialListItem>> {

        ArgumentValidator.notNullOrEmpty(request, 'request');

        const url = new QueryStringBuilder()
            .withPaginationIfNotEmpty(request.pagination)
            .build(`${this.apiUrl}/items/${request.entityType}/${request.entityId}/${request.itemType}/query`);

        const response = await this.axiosClient.post(
            url,
            request.query ? request.query : {},
            {
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            }
        );

        return response.data;
    }

    public async listParticipants(request: IEditorialListParticipantsByQueryRequest): Promise<IPagedParticipantList> {

        ArgumentValidator.notNullOrEmpty(request, 'request');

        const url = new QueryStringBuilder()
            .withPaginationIfNotEmpty(request.pagination)
            .withQueryIfNotEmpty('staleId', request.staleParticipantId)
            .build(`${this.apiUrl}/items/activity/${request.activityId}/participant/query`);

        const response = await this.axiosClient.post(
            url,
            {
                query: request.query.query,
                isEligibleVoter: request.query.isEligibleVoter,
                isAttending: request.query.isAttending,
                isSpectator: request.query.isSpectator,
                userIds: request.query.userIds,
                allowStaleStatistics: request.allowStaleStatistics
            },
            {
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            }
        );

        return response.data;
    }

    public async getStatus(request: IEditorialListReferenceRequest): Promise<IEditorialListStatus> {

        ArgumentValidator.notNullOrEmpty(request, 'request');

        const response = await this.axiosClient.get(
            `${this.apiUrl}/items/${request.entityType}/${request.entityId}/${request.itemType}/status`
        );

        return response.data;
    }

    public async addReference(request: IEditorialListNewReferenceRequest): Promise<IEditorialListItem> {

        ArgumentValidator.notNullOrEmpty(request, 'request');

        const response = await this.axiosClient.post(
            `${this.apiUrl}/items/${request.entityType}/${request.entityId}/${request.itemType}/reference`,
            request.item,
            {
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            }
        );

        return response.data;
    }

    public async addCandidatures(request: IEditorialListNewItemRequest): Promise<IEditorialListItemsByUsersQueryResult> {

        ArgumentValidator.notNullOrEmpty(request, 'request');

        const response = await this.axiosClient.post(
            `${this.apiUrl}/items/election/${request.electionId}/candidatures`,
            request.candidatures,
            {
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            }
        );

        return response.data;
    }

    public async addUsersByQuery(request: IEditorialListAddByQueryRequest): Promise<IEditorialListItemsByUsersQueryResult> {

        ArgumentValidator.notNullOrEmpty(request, 'request');

        const response = await this.axiosClient.post(
            `${this.apiUrl}/items/${request.entityType}/${request.entityId}/${request.itemType}/${request.pathRestrictionScope}/addUsersByQuery`,
            { 'searchRequest': request.query },
            {
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            }
        );

        return response.data;
    }

    public async addUsersByIdentifiers(request: IEditorialListAddByIdentifiersRequest): Promise<IEditorialListItemsByIdentifiersResult> {

        ArgumentValidator.notNullOrEmpty(request, 'request');

        const response = await this.axiosClient.post(
            `${this.apiUrl}/items/${request.entityType}/${request.entityId}/${request.itemType}/addUsersByIdentifiers?whatIf=` + request.whatIf,
            { 'identifiers': request.requestBody.identifiers },
            {
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            }
        );

        return response.data;
    }

    public async listUsers(request: IEditorialListUsersRequest): Promise<IPagedList<IUser>> {

        const url = new QueryStringBuilder()
            .withPaginationIfNotEmpty(request.pagination)
            .build(`${this.apiUrl}/items/${request.entityType}/${request.entityId}/${request.itemType}/${request.pathRestrictionScope}/listUsers`);

        const response = await this.axiosClient.post(
            url,
            request.query,
            {
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            }
        );

        return response.data;
    }
}
